import React from 'react'
import { motion } from 'framer-motion'
import { Activity, Anchor, BatteryCharging, Cpu } from 'lucide-react'
export function TechnologySection() {
  const features = [
    {
      icon: <Activity className="h-6 w-6" />,
      title: 'Kinetic Conversion',
      description:
        'Captures the vertical and horizontal motion of waves with high efficiency.',
    },
    {
      icon: <Anchor className="h-6 w-6" />,
      title: 'Resilient Mooring',
      description:
        'Advanced anchoring systems designed to withstand extreme storm conditions.',
    },
    {
      icon: <Cpu className="h-6 w-6" />,
      title: 'Smart Control',
      description:
        'AI-driven tuning optimizes resistance in real-time for maximum energy capture.',
    },
    {
      icon: <BatteryCharging className="h-6 w-6" />,
      title: 'Grid Integration',
      description:
        'Seamless power conditioning for direct connection to local utility grids.',
    },
  ]
  return (
    <section
      id="technology"
      className="py-24 bg-ocean-50 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-ocean-200 rounded-full blur-3xl opacity-50" />
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-ocean-500 rounded-full blur-3xl opacity-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-ocean-900 font-semibold tracking-wider uppercase text-sm mb-3">
            Our Technology
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold text-navy-900 mb-6">
            Engineered for the Ocean Environment
          </h3>
          <p className="text-lg text-gray-600">
            We utilize proven Point Absorber WEC technology that converts the
            motion of waves into clean electricity. Our systems are modular,
            scalable, and designed for minimal environmental impact.
          </p>
        </div>

        {/* Diagram / Visual Area */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-16 border border-ocean-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h4 className="text-2xl font-bold text-navy-900 mb-4">
                How It Works
              </h4>
              <ol className="space-y-6">
                <li className="flex">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-ocean-900 text-white font-bold text-sm mr-4">
                    1
                  </span>
                  <div>
                    <h5 className="font-semibold text-navy-900">
                      Wave Interaction
                    </h5>
                    <p className="text-gray-600 text-sm mt-1">
                      The float moves with the ocean waves, capturing kinetic
                      energy.
                    </p>
                  </div>
                </li>
                <li className="flex">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-ocean-900 text-white font-bold text-sm mr-4">
                    2
                  </span>
                  <div>
                    <h5 className="font-semibold text-navy-900">
                      Power Take-Off (PTO)
                    </h5>
                    <p className="text-gray-600 text-sm mt-1">
                      Mechanical motion drives a generator to produce
                      electricity.
                    </p>
                  </div>
                </li>
                <li className="flex">
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-ocean-900 text-white font-bold text-sm mr-4">
                    3
                  </span>
                  <div>
                    <h5 className="font-semibold text-navy-900">
                      Transmission
                    </h5>
                    <p className="text-gray-600 text-sm mt-1">
                      Subsea cables transmit power to the shore for grid
                      distribution.
                    </p>
                  </div>
                </li>
              </ol>
            </div>

            {/* Technical Diagram SVG */}
            <div className="order-1 md:order-2 flex justify-center">
              <svg
                viewBox="0 0 400 300"
                className="w-full max-w-md drop-shadow-lg"
              >
                {/* Water Line */}
                <path
                  d="M0,150 Q50,140 100,150 T200,150 T300,150 T400,150"
                  fill="none"
                  stroke="#088395"
                  strokeWidth="2"
                />
                <path
                  d="M0,150 Q50,140 100,150 T200,150 T300,150 T400,150 V300 H0 Z"
                  fill="#E8F4F8"
                  opacity="0.5"
                />

                {/* WEC Device */}
                <g transform="translate(200, 150)">
                  {/* Float */}
                  <circle
                    cx="0"
                    cy="0"
                    r="30"
                    fill="#0A4D68"
                    stroke="#fff"
                    strokeWidth="2"
                  >
                    <animate
                      attributeName="cy"
                      values="-10;10;-10"
                      dur="4s"
                      repeatCount="indefinite"
                    />
                  </circle>
                  {/* Tether */}
                  <line
                    x1="0"
                    y1="30"
                    x2="0"
                    y2="120"
                    stroke="#2C3E50"
                    strokeWidth="4"
                    strokeDasharray="4 2"
                  >
                    <animate
                      attributeName="y1"
                      values="20;40;20"
                      dur="4s"
                      repeatCount="indefinite"
                    />
                  </line>
                  {/* Base */}
                  <rect
                    x="-20"
                    y="120"
                    width="40"
                    height="20"
                    rx="4"
                    fill="#2C3E50"
                  />
                </g>

                {/* Cable */}
                <path
                  d="M220,280 C250,280 300,260 400,260"
                  fill="none"
                  stroke="#00C9A7"
                  strokeWidth="3"
                  strokeDasharray="4"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{
                opacity: 0,
                y: 20,
              }}
              whileInView={{
                opacity: 1,
                y: 0,
              }}
              viewport={{
                once: true,
              }}
              transition={{
                delay: index * 0.1,
                duration: 0.5,
              }}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
            >
              <div className="w-12 h-12 bg-ocean-50 rounded-lg flex items-center justify-center text-ocean-900 mb-4">
                {feature.icon}
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-2">
                {feature.title}
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
